// /swiftboard/estimates/new/:jobId — Load Takeoff → Map Surfaces → Project Setup.
// All wizard state lives here so going Back never loses work.
import { useCallback, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calculator } from "lucide-react";
import NoDataState from "modules/swiftboard/components/NoDataState";
import Skeleton from "components/ui/Skeleton";
import { AuthContext } from "context/AuthContext";
import { useAlert } from "context/AlertContext";
import { useBreadcrumbLabels } from "utils/breadcrumb-labels";
import useAsync from "../../hooks/useAsync";
import { getConfig, getDefaultProject, getJob, saveMappings } from "../../mock/estimateService.mock";
import { Button, Card } from "../../components/ui";
import { PrototypeBadge } from "../../components/EstimateBadges";
import { ErrorState } from "../../components/PageStates";
import Stepper from "./Stepper";
import StepLoadTakeoff from "./StepLoadTakeoff";
import StepMapSurfaces from "./StepMapSurfaces";
import StepProjectSetup from "./StepProjectSetup";
import { initMappings, mappingStats } from "./map-surfaces/mapping-utils";

const STEPS = [
  { label: "Load Takeoff", hint: "Parse the attached file" },
  { label: "Map Surfaces", hint: "Confirm a surface per line" },
  { label: "Project Setup", hint: "Crew, brand & bid inputs" },
];

function WizardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-4 p-4">
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-8 w-full" />
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4">
          <Skeleton className="h-14 w-full" />
          <Skeleton.Rows rows={2} />
        </Card>
        <Card className="p-4">
          <Skeleton className="h-[180px] w-full" />
        </Card>
      </div>
    </div>
  );
}

export default function EstimateWizard() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();

  const { data, loading, error, reload } = useAsync(
    async () => {
      const [job, config, defaultProject] = await Promise.all([getJob(jobId), getConfig(), getDefaultProject()]);
      return { job, config, defaultProject };
    },
    [jobId]
  );

  const [step, setStep] = useState(0);
  const [parse, setParse] = useState(null);
  const [mappings, setMappings] = useState({});
  const [skipUnmapped, setSkipUnmapped] = useState(false);
  const [remember, setRemember] = useState(false);
  const [projectEdits, setProjectEdits] = useState(null);
  const [continuing, setContinuing] = useState(false);

  const job = data?.job;
  useBreadcrumbLabels(job ? { new: "", [jobId]: `Job ${jobId} – ${job.title}` } : { new: "" });

  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const project = data ? { ...data.defaultProject, estimator: userName || data.defaultProject.estimator, ...projectEdits } : null;
  const setProject = useCallback((patch) => setProjectEdits((p) => ({ ...p, ...patch })), []);

  const handleParsed = (result) => {
    setParse(result);
    setMappings(initMappings(result.lines));
    setSkipUnmapped(false);
  };

  const stats = parse ? mappingStats(parse.lines, mappings) : null;
  const mapDone = !!stats && stats.mapped > 0 && (stats.unmapped === 0 || skipUnmapped);
  const maxReachable = !parse ? 0 : mapDone ? 2 : 1;

  const continueFromMapping = async () => {
    if (remember) {
      setContinuing(true);
      try {
        const map = Object.fromEntries(parse.lines.filter((l) => mappings[l.id]?.surfaceId).map((l) => [l.rawName, mappings[l.id].surfaceId]));
        const { saved } = await saveMappings(map);
        showAlert({ type: "success", message: `${saved} mappings remembered for future jobs` });
      } catch (e) {
        showAlert({ type: "error", message: e.message || "Could not save mappings" });
      } finally {
        setContinuing(false);
      }
    }
    setStep(2);
  };

  const renderBody = () => {
    if (loading) return <WizardSkeleton />;
    if (error) return <ErrorState error={error} onRetry={reload} title="Could not load this job" />;
    if (!job.takeoffFile) {
      return (
        <Card className="flex items-center justify-center">
          <NoDataState
            icon="file"
            title="No takeoff file on this job – upload one"
            message="Estimates are built from a takeoff export (.xlsx / .csv). Ask the client to attach it under the job's Files tab, then come back here."
            showRefresh={false}
            showBack
            actionText="Back to Live Projects"
            onBack={() => navigate("/swiftboard/live-projects")}
            size="medium"
          />
        </Card>
      );
    }
    return (
      <>
        <Card className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[4px] bg-primary_tint text-primary">
                <Calculator size={18} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h1 className="truncate text-[16px] font-semibold text-text_primary">New Estimate · Job {job.jobId} – {job.title}</h1>
                <p className="truncate text-[12px] text-text_muted">Client: {job.clientName}</p>
              </div>
            </div>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate("/swiftboard/estimates")}>
              All estimates
            </Button>
          </div>
          <Stepper steps={STEPS} current={step} maxReachable={maxReachable} onStepClick={setStep} />
        </Card>

        {step === 0 && <StepLoadTakeoff job={job} parse={parse} onParsed={handleParsed} onContinue={() => setStep(1)} />}
        {step === 1 && parse && (
          <StepMapSurfaces
            lines={parse.lines}
            config={data.config}
            mappings={mappings}
            setMappings={setMappings}
            skipUnmapped={skipUnmapped}
            setSkipUnmapped={setSkipUnmapped}
            remember={remember}
            setRemember={setRemember}
            onBack={() => setStep(0)}
            onContinue={continueFromMapping}
            continuing={continuing}
          />
        )}
        {step === 2 && parse && (
          <StepProjectSetup
            jobId={jobId}
            project={project}
            setProject={setProject}
            config={data.config}
            lines={parse.lines}
            mappings={mappings}
            onBack={() => setStep(1)}
          />
        )}
      </>
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-[100rem] flex-col gap-4 pt-3">
        {renderBody()}
        <div className="h-4 flex-shrink-0" aria-hidden="true" />
      </div>
      <PrototypeBadge />
    </div>
  );
}
